// SELLER — the Ägarbyte task, ported from the reference (components/ownership/SellerOwnershipTask.tsx).
//
// The ownership transfer is the seller's task, and it is done at Transportstyrelsen, not in
// Klargo: the stage says so, lays out exactly what the e-service asks about the new owner, opens
// the service, and offers the one statement that moves the deal on for both parties.
//
// The rows follow Transportstyrelsen's public instructions for the e-service (the seller signs in
// with e-legitimation; both parties' Swedish driving licences are given by validity date and
// reference number; the new owner is identified by personnummer). Identity values are masked until
// the seller asks to see them, and a copy always takes the whole value, shown or not — the seller
// is typing these into another service, not reading them off the screen.
//
// WHERE EACH ROW COMES FROM. The new owner's name, their personal identity number and the
// registration number are the deal's own data, read from the database through
// ContractOwnership/GetState. The two DRIVING LICENCE rows are stood in for: Klargo stores a
// licence image per user (AbpUsers.DrivingLiscencePath) and whether it was accepted, but neither
// the validity date nor the reference number is recorded anywhere, so there is nothing to read.
// They are marked as stand-ins on screen rather than presented as the buyer's real licence.
import React, { useState } from "react";
import { ExternalLink, Loader2 } from "lucide-react";
import RegistrationPlate from "../../common/RegistrationPlate";
import { Notice } from "../payment/PaymentParts";
import { H1_CLASS, LEAD_CLASS, META_CLASS, OwnershipKicker, OwnershipLedger } from "./OwnershipParts";

/**
 * Transportstyrelsen's own page for a private ownership transfer: the instructions and the way
 * into the e-service (Mina sidor). Never the authority's start page — the seller arrives where
 * the task is.
 */
export const TRANSPORTSTYRELSEN_OWNERSHIP_URL = "https://www.transportstyrelsen.se/agarbyte";

// STAND-INS — no field holds these. Klargo records that a driving licence was seen and accepted,
// never its validity date or its reference number, so these two rows cannot be read from the
// database. The same applies to the personal identity number on an account that never identified
// with BankID (AbpUsers.BankIdSSN empty), which the server reports as absent rather than blank.
// Each is said to be a stand-in where it is shown, because a seller must not type an invented
// number into Transportstyrelsen.
const PLACEHOLDER_LICENCE_VALID_UNTIL = "2031-05-14";
const PLACEHOLDER_LICENCE_REFERENCE = "123456789";
const PLACEHOLDER_NATIONAL_ID = "19900823-0000";

/** "123456789" → "123 456 789". Display only; the copy is the plain number. */
const groupDigits = (value) => String(value).replace(/(\d{3})(?=\d)/g, "$1 ");

/** Everything but the last four characters, which are the ones worth hiding on a shared screen. */
const maskTail = (value) => {
  const text = String(value);
  return text.length <= 4 ? "••••" : `${text.slice(0, text.length - 4)}••••`;
};

export default function SellerOwnershipTask({
  buyerName, plate, nationalId, onAssert, asserting, failed, headingRef, t,
}) {
  const [revealed, setRevealed] = useState(false);
  // Seller-local presentation only: once the service has been opened, the next expected act is the
  // statement, so the ink moves there. It is never shared — the seller may equally have done the
  // transfer on another device.
  const [linkUsed, setLinkUsed] = useState(false);

  /** No identity number on this deal: what is shown stands in for it, and the notice says so. */
  const idMissing = !nationalId;
  const shownId = nationalId ?? PLACEHOLDER_NATIONAL_ID;

  const rows = [
    { key: "owner", label: t("ownership.ledger.newOwner"), value: buyerName },
    {
      key: "national-id",
      label: t("ownership.ledger.nationalId"),
      value: revealed ? shownId : maskTail(shownId),
      copyValue: shownId,
    },
    {
      key: "licence-valid",
      label: t("ownership.ledger.licenceValid"),
      value: PLACEHOLDER_LICENCE_VALID_UNTIL,
      copyValue: PLACEHOLDER_LICENCE_VALID_UNTIL,
    },
    {
      key: "licence-reference",
      label: t("ownership.ledger.licenceRef"),
      value: revealed ? groupDigits(PLACEHOLDER_LICENCE_REFERENCE) : "••• ••• •••",
      copyValue: PLACEHOLDER_LICENCE_REFERENCE,
    },
    {
      key: "reg-number",
      label: t("ownership.ledger.regNumber"),
      value: <RegistrationPlate value={plate} size="row" />,
      copyValue: plate,
    },
  ];

  const openLabel = linkUsed ? t("ownership.seller.openAgain") : t("ownership.seller.open");
  const externalLink = (
    <a
      href={TRANSPORTSTYRELSEN_OWNERSHIP_URL}
      target="_blank"
      rel="noopener noreferrer"
      onClick={() => setLinkUsed(true)}
      aria-label={`${openLabel} (${t("ownership.newTab")})`}
      data-testid="ownership-open-transportstyrelsen"
      className={linkUsed
        ? "ds-action-text min-h-[44px]"
        : "ds-action-ink w-full justify-center no-underline sm:w-auto sm:min-w-[260px]"}
    >
      <span className={linkUsed ? "ds-underline" : undefined}>{openLabel}</span>
      <ExternalLink className="h-4 w-4" aria-hidden="true" />
    </a>
  );

  return (
    <div data-testid="ownership-seller-task">
      <OwnershipKicker reported={false} t={t} />
      <h1 ref={headingRef} tabIndex={-1} className={`ds-arrival-heading ${H1_CLASS} focus:outline-none`}>
        {t("ownership.seller.title")}
      </h1>
      <p className={LEAD_CLASS}>{t("ownership.seller.lead", { buyer: buyerName })}</p>

      <OwnershipLedger
        rows={rows}
        t={t}
        header={
          <div className="flex items-end justify-between gap-4 pb-1.5">
            <h2 className="m-0 font-ds-sans text-[13.5px] font-semibold text-ds-ink">{t("ownership.ledger.title")}</h2>
            <button type="button" onClick={() => setRevealed((shown) => !shown)} aria-pressed={revealed}
              data-testid="ownership-reveal" className="ds-action-text ds-hit -my-2 min-h-[44px] text-[13px]">
              <span className="ds-underline">{revealed ? t("ownership.ledger.hide") : t("ownership.ledger.show")}</span>
            </button>
          </div>
        }
      />
      {/* Said once, beside the rows it is about: a seller must not report a transfer against a
          number Klargo never had. The task stays on screen — the seller has the buyer in front of
          them and can read the real numbers off the licence. */}
      {idMissing && (
        <Notice tone="red" testId="ownership-id-placeholder">{t("ownership.seller.idPlaceholder")}</Notice>
      )}
      <p className={`${META_CLASS} mt-3`}>{t("ownership.seller.own")}</p>
      {/* The two licence rows are never the buyer's real licence: no field holds them. */}
      <p className={`${META_CLASS} mt-1`} data-testid="ownership-licence-placeholder">
        {t("ownership.seller.licencePlaceholder")}
      </p>

      <div className="mt-6 flex flex-col items-start gap-4">
        {!linkUsed && externalLink}
        <div className="w-full">
          <p className={`${META_CLASS} mb-3`}>{t("ownership.seller.responsibility")}</p>
          <button type="button" onClick={onAssert} disabled={asserting} data-testid="ownership-assert"
            className={`${linkUsed ? "ds-action-ink" : "ds-action-outline"} w-full justify-center sm:w-auto sm:min-w-[260px]`}>
            {asserting && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
            {t("ownership.seller.assert")}
          </button>
          {failed && (
            <p role="alert" className="m-0 mt-2.5 text-[13.5px] leading-[1.5] text-ds-red-fg">
              {failed === true ? t("ownership.error") : failed}
            </p>
          )}
        </div>
        {linkUsed && externalLink}
      </div>
    </div>
  );
}
